import { useState } from 'react';

import { errorMessage } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';

import { JobForm, toJobCreate, useForemen } from './JobForm';
import { useJobContext } from './JobDetailPage';
import { ReconcileDialog } from './ReconcileDialog';
import { usePatchJob, useSetForemen } from './useJobs';

/** W3 Settings: edit job fields, assign foremen, Reconcile for field-created jobs. */
export function JobSettingsTab() {
  const { job } = useJobContext();
  const patch = usePatchJob(job.id);
  const setForemen = useSetForemen(job.id);
  const foremen = useForemen();
  const toast = useToast();
  const [reconcile, setReconcile] = useState(false);
  const assigned = new Set(job.foremen.map((f) => f.id));

  return (
    <div className="two">
      <Card>
        <CardHeader title="Job" />
        <CardBody>
          <JobForm
            key={job.updatedAt}
            job={job}
            withForemen={false}
            submitLabel="Save changes"
            pending={patch.isPending}
            onSubmit={(v) => {
              const { foremanIds: _f, ...rest } = toJobCreate(v);
              patch.mutate(rest, {
                onSuccess: () => toast.success('Job saved'),
                onError: (e) => toast.error(errorMessage(e)),
              });
            }}
          />
        </CardBody>
      </Card>
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader title="Foremen" />
          <CardBody className="flex flex-col gap-2">
            <p className="text-xs text-ink3">
              Assigned foremen see this job on their phone and can log days against it.
            </p>
            {foremen.data?.items.map((f) => (
              <label key={f.id} className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={assigned.has(f.id)}
                  disabled={setForemen.isPending}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...assigned, f.id]
                      : [...assigned].filter((x) => x !== f.id);
                    setForemen.mutate(next, {
                      onSuccess: () => toast.success('Foremen updated'),
                      onError: (err) => toast.error(errorMessage(err)),
                    });
                  }}
                />
                {f.name}
              </label>
            ))}
          </CardBody>
        </Card>
        {job.createdByForeman ? (
          <Card>
            <CardHeader title="Created in the field" />
            <CardBody>
              <p className="mb-3 text-[13px] text-ink2">
                {job.foremen[0]?.name ?? 'A foreman'} created this job from the phone. Merge it into
                the real job so its entries land in the right place.
              </p>
              <Button variant="primary" onClick={() => setReconcile(true)}>
                Reconcile into another job…
              </Button>
            </CardBody>
          </Card>
        ) : null}
      </div>
      <ReconcileDialog job={job} open={reconcile} onClose={() => setReconcile(false)} />
    </div>
  );
}
